import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface ChartEmptyStateProps {
  isLoading: boolean;
  error: Error | null;
  isEmpty: boolean;
  height: number;
  title: string;
  noDataTitle?: string;
  noDataDescription?: string;
}

const ChartEmptyState: React.FC<ChartEmptyStateProps> = ({
  isLoading,
  error,
  isEmpty,
  height,
  title,
  noDataTitle,
  noDataDescription,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const textSecondary = isDark ? "#9ca3af" : "#64748b";
  const textMuted = isDark ? "#6b7280" : "#94a3b8";

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={height}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const is404 =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error as any)?.response?.status === 404 ||
      error.message?.includes("404");

    const errorTitle = is404
      ? (noDataTitle ?? `No ${title} Data Available`)
      : `Unable to Load ${title} Data`;

    const errorDescription = is404
      ? (noDataDescription ??
        `${title} data is not available for this company, which is common for smaller companies, recent IPOs, or international stocks.`)
      : "We encountered an issue loading data. Please try again later.";

    return (
      <Box
        sx={{
          py: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" sx={{ color: textSecondary, mb: 2 }}>
          {errorTitle}
        </Typography>
        <Typography sx={{ color: textMuted, maxWidth: "500px" }}>
          {errorDescription}
        </Typography>
      </Box>
    );
  }

  if (isEmpty) {
    return (
      <Box
        sx={{
          py: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" sx={{ color: textSecondary, mb: 2 }}>
          {noDataTitle ?? `No ${title} Data Available`}
        </Typography>
        <Typography sx={{ color: textMuted, maxWidth: "500px" }}>
          {noDataDescription ??
            `${title} data is not available for this company, which is common for smaller companies, recent IPOs, or international stocks.`}
        </Typography>
      </Box>
    );
  }

  return null;
};

export default ChartEmptyState;
